<template>
  <!-- 네이버 SDK가 표준 구매 버튼을 렌더링하는 전용 영역이다. -->
  <div class="naver-pay-button" :id="containerId"></div>
</template>

<script>
/* eslint-disable camelcase */
import {
  get_naver_pay_button_config,
  register_naver_pay_order
} from './Axios.vue'

export default {
  name: 'NaverPayButton',
  props: {
    // 현재 화면에서 선택한 상품과 옵션만 전달한다.
    buyList: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      // Vue 컴포넌트마다 고유한 DOM ID를 만들어 SDK 충돌을 방지한다.
      containerId: `npay-button-container-${this._uid}`,
      naverPayButton: null
    }
  },
  async mounted () {
    try {
      const config = await get_naver_pay_button_config()

      // SDK와 주문등록 API는 백엔드의 NAVER_PAY_ENVIRONMENT 하나로 함께 전환한다.
      // 프론트에서 주소만 바꾸면 테스트 주문등록과 운영 SDK가 섞일 수 있으므로 서버 설정을 사용한다.
      // 운영 키가 설정되지 않은 환경에서는 일반 구매 흐름을 방해하지 않는다.
      if (!config || !config.buttonKey || !config.buttonScriptUrl) {
        return
      }

      await this.loadNaverPaySdk(config.buttonScriptUrl)
      await this.createNaverPayButton(config)
    } catch (error) {
      // 버튼 실패는 브라우저 콘솔에만 기록하고 기존 구매 기능은 유지한다.
      console.error('네이버페이 버튼을 초기화하지 못했습니다.', error)
    }
  },
  beforeDestroy () {
    // SPA 페이지 전환 시 SDK 인스턴스를 정리해 중복 버튼과 이벤트 누수를 막는다.
    if (this.naverPayButton && typeof this.naverPayButton.dispose === 'function') {
      this.naverPayButton.dispose()
    }
  },
  methods: {
    loadNaverPaySdk (scriptUrl) {
      if (window.Npay && window.Npay.order) {
        return Promise.resolve()
      }

      return new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-naver-pay-sdk="true"]')

        if (existingScript) {
          existingScript.addEventListener('load', resolve, { once: true })
          existingScript.addEventListener('error', reject, { once: true })
          return
        }

        const script = document.createElement('script')
        script.src = scriptUrl
        script.async = true
        script.dataset.naverPaySdk = 'true'
        script.onload = resolve
        script.onerror = () => reject(new Error('네이버페이 SDK를 불러오지 못했습니다.'))
        document.head.appendChild(script)
      })
    },
    async createNaverPayButton (config) {
      if (!window.Npay || !window.Npay.order) {
        throw new Error('네이버페이 SDK가 준비되지 않았습니다.')
      }

      this.naverPayButton = await window.Npay.order.create({
        // 버튼 키는 공개 가능한 식별 값이며, 가맹점 인증키는 서버에만 존재한다.
        buttonKey: config.buttonKey,
        containerId: this.containerId,
        orderRegistrationVersion: config.orderRegistrationVersion,
        type: 'template',
        colorTheme: 'green',
        enable: true,
        components: {
          // 찜하기 API는 별도 연동 범위이므로 구매 버튼만 먼저 제공한다.
          wishlist: false,
          talkTalk: false,
          benefitMessage: true,
          benefitCoachMark: true
        },
        /**
         * 상품상세·장바구니 공통 구매 버튼 클릭 처리
         *
         * orderRegistrationVersion '2.1' 기준 onBuyClick은 정확히 { key, merchantNo }를 반환해야
         * 버튼 SDK가 네이버페이 주문서를 연다. 백엔드 주문등록 응답은 { buyKey, merchantNo }이므로
         * 그대로 반환하면 주문서가 열리지 않는다. (네이버페이 검수 지적 2번)
         */
        onBuyClick: async () => {
          // 주문 데이터를 백엔드로 전달해 DB 기준 가격과 옵션으로 다시 검증한다.
          const orderResult = await register_naver_pay_order({
            buyList: this.buyList,
            backUrl: this.createBackUrl(),
            // 네이버 공통 유입 스크립트(index.html)가 만든 쿠키. 없으면 빈 값으로 전달한다.
            naverInflowCode: this.getCookie('NA_CO'),
            saClickId: this.getCookie('NVADID')
          })

          if (!orderResult || !orderResult.buyKey || !orderResult.merchantNo) {
            const message = '네이버페이 주문등록 응답이 올바르지 않습니다. (주문등록 인증키 또는 가맹점 번호 누락)'

            alert(message)
            throw new Error(message)
          }

          return {
            key: orderResult.buyKey,
            merchantNo: orderResult.merchantNo
          }
        }
      })
    },
    /**
     * 주문서의 "이전" 버튼으로 돌아올 주소를 만든다.
     * NaPm, NVADID 등 광고·유입 추적 파라미터와 hash는 backUrl에 넣지 않는다. (네이버페이 검수 지적 3번)
     * 백엔드도 허용 목록(product_ID) 방식으로 한 번 더 정리하므로, 여기서는 경로와 product_ID만 남긴다.
     */
    createBackUrl () {
      try {
        const currentUrl = new URL(window.location.href)
        const backUrl = new URL(currentUrl.pathname, currentUrl.origin)
        const productId = currentUrl.searchParams.get('product_ID')

        if (productId) {
          backUrl.searchParams.set('product_ID', productId)
        }

        return backUrl.toString()
      } catch (error) {
        // URL API를 지원하지 않는 구형 브라우저는 쿼리를 모두 제거하고 백엔드 정리에 맡긴다.
        return `${window.location.origin || ''}${window.location.pathname}`
      }
    },
    getCookie (name) {
      const cookiePrefix = `${name}=`
      const cookie = document.cookie.split(';').map(item => item.trim()).find(item => item.indexOf(cookiePrefix) === 0)

      return cookie ? decodeURIComponent(cookie.substring(cookiePrefix.length)) : ''
    }
  }
}
</script>

<style scoped>
.naver-pay-button {
  min-height: 150px;
  margin-top: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/ETC/NaverPayButton.vue